import { useState } from 'react';
import { Wrench, Lock, RotateCw, Upload, Download, History } from 'lucide-react';
import { toast } from 'react-toastify';

import Breadcrumb from '../../../components/Breadcrumb/Breadcrumb';
import Tabs from '../../../components/Tabs/Tabs';
import Table, { StatusPill } from '../../../components/Table/Table';

import usePayrollEntity from '../../../utils/usePayrollEntity';

const ICON = { size: 15 };

const TABLE_PROPS = {
  search: true,
  searchLabel: 'Search',
  pageSize: 10,
  scrollClassName: '',
};

/* Same on/off pill used on Master File Maintenance's Configuration tab. */
const Toggle = ({ on, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    role="switch"
    aria-checked={on}
    className={`relative w-10 h-5 shrink-0 rounded-full transition-colors ${
      on ? 'bg-primary-600' : 'bg-gray-200'
    }`}
  >
    <span
      className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow-sm transition-transform ${
        on ? 'translate-x-5' : ''
      }`}
    />
  </button>
);

/* ── Utility Actions ─────────────────────────────────────────────────────── */
const ActionsTab = () => {
  const [locked, setLocked] = useState(false);

  const importTargets = ['Salary Components', 'Overtime', 'Allowances', 'Deductions'];

  const toggleLock = () => {
    const next = !locked;
    setLocked(next);
    toast.info(next ? 'Payroll period locked.' : 'Payroll period unlocked.', { autoClose: 1800 });
  };

  const reprocess = () => toast.success('Payroll reprocessing started.', { autoClose: 2000 });
  const importData = (target) => toast.info(`Import ${target} (demo).`, { autoClose: 2000 });
  const exportData = () => toast.info('Exporting payroll data (demo).', { autoClose: 2000 });

  return (
    <div className="flex flex-col gap-4">
      <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-5">
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <Lock size={18} className="text-primary-700 shrink-0" />
            <div>
              <div className="text-[14px] font-semibold text-gray-900">Payroll Lock / Unlock</div>
              <div className="text-[13px] text-gray-500">
                {locked
                  ? 'The current payroll period is locked. No further edits are allowed.'
                  : 'The current payroll period is open for edits.'}
              </div>
            </div>
          </div>
          <Toggle on={locked} onClick={toggleLock} />
        </div>
      </div>

      <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-5">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-3">
            <RotateCw size={18} className="text-primary-700 shrink-0" />
            <div>
              <div className="text-[14px] font-semibold text-gray-900">Reprocess Payroll</div>
              <div className="text-[13px] text-gray-500">
                Re-run calculations for the current period after a correction.
              </div>
            </div>
          </div>
          <button
            type="button"
            onClick={reprocess}
            className="inline-flex items-center h-9 px-4 rounded-lg text-[13px] font-semibold transition-colors
              border border-primary-600 text-primary-700 hover:bg-primary-50"
          >
            Reprocess
          </button>
        </div>
      </div>

      <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-5">
        <div className="text-[14px] font-semibold text-gray-900 mb-3">Import Data</div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {importTargets.map((target) => (
            <button
              key={target}
              type="button"
              onClick={() => importData(target)}
              className="flex items-center gap-2 h-10 px-3 rounded-lg text-[13px] font-medium text-gray-700
                border border-gray-200 hover:bg-gray-50 transition-colors"
            >
              <Upload size={15} className="text-gray-500 shrink-0" />
              Import {target}
            </button>
          ))}
        </div>
      </div>

      <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-5">
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <Download size={18} className="text-primary-700 shrink-0" />
            <div>
              <div className="text-[14px] font-semibold text-gray-900">Export Payroll Data</div>
              <div className="text-[13px] text-gray-500">
                Export the full payroll dataset for the current period.
              </div>
            </div>
          </div>
          <button
            type="button"
            onClick={exportData}
            className="inline-flex items-center h-9 px-4 rounded-lg text-[13px] font-semibold transition-colors
              bg-primary-600 text-white hover:bg-primary-700"
          >
            Export
          </button>
        </div>
      </div>
    </div>
  );
};

/* ── Audit Logs ──────────────────────────────────────────────────────────── */
const AuditLogsTab = () => {
  const { list, loading } = usePayrollEntity('auditLogs');

  return (
    <Table
      {...TABLE_PROPS}
      title="Audit Logs"
      loading={loading}
      rows={list}
      rowKey="id"
      searchKeys={['action', 'performedBy', 'module']}
      searchPlaceholder="Search audit logs..."
      emptyMessage="No audit logs found"
      columns={[
        { key: 'action', label: 'Action', grow: true, className: 'font-semibold text-gray-900' },
        { key: 'module', label: 'Module', className: 'text-gray-600' },
        { key: 'performedBy', label: 'Performed By', className: 'text-gray-600' },
        { key: 'timestamp', label: 'Timestamp', className: 'text-gray-600 tabular-nums' },
      ]}
    />
  );
};

/* ── Import / Export History ─────────────────────────────────────────────── */
const ImportExportLogTab = () => {
  const { list, loading } = usePayrollEntity('importExportLogs');

  return (
    <Table
      {...TABLE_PROPS}
      title="Import / Export History"
      loading={loading}
      rows={list}
      rowKey="id"
      searchKeys={['type', 'target', 'fileName']}
      searchPlaceholder="Search import/export history..."
      emptyMessage="No import/export history found"
      columns={[
        { key: 'type', label: 'Type', className: 'text-gray-600' },
        { key: 'target', label: 'Target', grow: true, className: 'font-semibold text-gray-900' },
        { key: 'fileName', label: 'File', className: 'text-gray-600' },
        { key: 'date', label: 'Date', className: 'text-gray-600 tabular-nums' },
        {
          key: 'status',
          label: 'Status',
          align: 'center',
          render: (r) => <StatusPill label={r.status} />,
        },
      ]}
    />
  );
};

/* ── Page ────────────────────────────────────────────────────────────────── */
const Utilities = () => {
  const tabs = [
    { key: 'actions', label: 'Utility Actions', icon: <Wrench {...ICON} />, content: <ActionsTab /> },
    { key: 'audit', label: 'Audit Logs', icon: <History {...ICON} />, content: <AuditLogsTab /> },
    { key: 'importExport', label: 'Import / Export History', icon: <Upload {...ICON} />, content: <ImportExportLogTab /> },
  ];

  return (
    <div className="p-6 space-y-4">
      <Breadcrumb
        trail={['Payroll', 'Utilities']}
        title="Utilities"
        subtitle="Lock periods, reprocess payroll, review audit logs and manage bulk import/export."
      />
      <Tabs tabs={tabs} defaultTab="actions" />
    </div>
  );
};

export default Utilities;